/**
 * The affiliation notice: a full-width strip above the masthead stating, in the
 * first line on the page, that this is not a government site.
 *
 * WHY IT EXISTS. `Header.jsx` and `ds-core.css` both used to claim, in
 * comments, that the masthead tagline made "not a DOE site" read before
 * anything else on the page. The tagline read "archives of technical
 * standards" and made no such claim -- the job was documented for months but
 * never implemented. BL-0080 established that the live exposure is implied
 * government affiliation, "sharpest here precisely because the sites *were* the
 * official systems and are now actively trying to out-rank them", and BL-0112
 * then put the agency name into every page title. `SiteConfig.seoName` records
 * the condition attached to that: the agency name is licensed by the
 * disclaimer surfaces, and the surface a visitor sees FIRST was the one
 * missing. /about has carried the disclaimer since BL-0082, but nobody lands
 * on /about.
 *
 * WHY THIS WORDING. Modelled on GovTrack.us, which has the same problem -- an
 * independent site about government whose visitors arrive assuming otherwise.
 * It reads: "GovTrack.us is not a government website. We're a small, wholly
 * independent organization." Three properties were copied deliberately, after
 * four in-house drafts were rejected for reading as glued together:
 *
 *   - COMPLETE SUBJECT-VERB SENTENCES. A fragment ("Independent archive --
 *     not affiliated with...") has no verb, so it can only join what follows
 *     with punctuation. That is precisely what made the rejected drafts read
 *     as parts bolted together rather than prose.
 *   - NO EM DASHES. Full stops carry the joins.
 *   - THE LINK IS A SENTENCE WITH A VERB, not a noun phrase after a full stop.
 *     Compare eCFR's "Learn more about the eCFR, its status, and the editorial
 *     process" with a bare trailing "About this archive".
 *
 * The phrasing also solves a per-site problem for free: "not a government
 * website" disavows NNSA and DOE at once, so unlike `SiteConfig.disclaimer`
 * this needs no nnsa-specific variant.
 *
 * The sentence is BUILT from `seoName` and `PUBLISHER_LEGAL_NAME` rather than
 * stored as its own config string, so it cannot drift from the titles whose
 * agency name it licenses.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useSiteConfig } from '../../helpers/useSiteConfig';
import { PUBLISHER_LEGAL_NAME } from '../../siteConfig/branding';

const AffiliationNotice = () => {
  const site = useSiteConfig();
  return (
    // aside, not a div with role="note" (TAIUIT item 15). Header.jsx
    // renders this as a sibling BEFORE <header>, so its content sat
    // outside every landmark on every page. role="note" cannot be kept
    // alongside: it overrides the implicit complementary role and puts
    // the content straight back outside the landmark structure.
    <aside className="pp-affiliation" aria-label="Site affiliation notice">
      <p className="pp-affiliation__inner">
        <strong className="pp-affiliation__lead">
          This is not a government website.
        </strong>{' '}
        The {site.seoName} is published independently by {PUBLISHER_LEGAL_NAME}.{' '}
        <Link to="/about">Learn more about this archive</Link>.
      </p>
    </aside>
  );
};

export default AffiliationNotice;
